import { ActivitiesConfigModal } from "portal/views/autotractor/jobs/orchestrate/ActivitiesConfigModal";
import { Alert, Button, Divider, Typography } from "@mui/material";
import { AlignAllRowsModal } from "../AlignAllRowsModal";
import { AutoCompleteRowsModal } from "../AutoCompleteRowsModal";
import { AutonomousActivityType } from "protos/atk/enums";
import { capitalize } from "portal/utils/strings";
import { classes } from "portal/theme/theme";
import { DateTime } from "luxon";
import { Job } from "protos/rtc/jobs";
import { jobExplorer } from "common/state/jobExplorer";
import { JobType } from "protos/atk/job_type";
import { M6ActivitiesConfigEditors } from "../M6ActivitiesConfigEditors";
import { RolloutChip } from "portal/components/RolloutChip";
import { rtcJobsApi } from "common/state/rtcJobsApi";
import { rtcLocatorApi } from "common/state/rtcLocatorApi";
import { SpeedEditModal } from "../SpeedEditModal";
import { useJobStateStatus, useJobTypeStatus } from "../../JobStatus";
import { useQueryPopups } from "portal/utils/hooks/useApiPopups";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import CalibrateIcon from "@mui/icons-material/ControlCamera";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import React, { FC, ReactNode, useMemo, useState } from "react";

interface JobDetailsProps {
  job: Job;
}

export const JobDetails: FC<JobDetailsProps> = ({ job: selectedJob }) => {
  const { t } = useTranslation();
  const farm = useSelector(jobExplorer.selectors.selectFarm);
  // the selection is a snapshot; the cached configured job stays live
  const { data: configuredJob } =
    rtcJobsApi.endpoints.getConfiguredJob.useQuery({
      id: selectedJob.id.toString(),
    });
  const job = configuredJob?.job ?? selectedJob;

  const [stateStatus] = useJobStateStatus(job);
  const [jobType] = useJobTypeStatus(job, undefined);

  const [showSpeedEditModal, setShowSpeedEditModal] = useState<boolean>(false);
  const [showAutoCompleteRowsModal, setShowAutoCompleteRowsModal] =
    useState<boolean>(false);
  const [showActivitiesConfigModal, setShowActivitiesConfigModal] =
    useState<boolean>(false);
  const [showAlignAllRowsModal, setShowAlignAllRowsModal] =
    useState<boolean>(false);

  const { data: availableTractors } = useQueryPopups(
    rtcLocatorApi.endpoints.listRobots.useQuery({
      serials: job.robotWhitelist?.entries.map((r) => r.robotSerial),
    })
  );

  const activities: AutonomousActivityType[] = useMemo(() => {
    // start with the activities present in the job tasks
    const relevantActivities = new Set<AutonomousActivityType>(
      job.activityTypes
    );

    // add any activities explicitly configured on the job
    if (configuredJob?.groundPrep?.pathPlan?.rigActivities) {
      configuredJob.groundPrep.pathPlan.rigActivities.activities.forEach(
        (activity) => {
          relevantActivities.add(activity.activity);
        }
      );
    }

    // spit all of the activities out in a canonical order based on UI design
    const displayActivities: AutonomousActivityType[] = [];

    for (const activity of [
      AutonomousActivityType.ACTIVITY_WORKING_ROW,
      AutonomousActivityType.ACTIVITY_WORKING_HEADLAND,
      AutonomousActivityType.ACTIVITY_TRANSPORT,
      AutonomousActivityType.ACTIVITY_COVERING_TRACKS,
      AutonomousActivityType.ACTIVITY_TRAVERSE,
      AutonomousActivityType.ACTIVITY_WORKING_DIVERT,
    ]) {
      if (relevantActivities.has(activity)) {
        displayActivities.push(activity);
      }
    }

    return displayActivities;
  }, [configuredJob?.groundPrep?.pathPlan?.rigActivities, job.activityTypes]);

  const rowElement = (
    label: ReactNode,
    content: ReactNode,
    {
      multiLine,
      variant = "primary",
    }: { multiLine?: boolean; variant?: "primary" | "secondary" } = {}
  ): ReactNode => {
    const row = (
      <div
        className={classes(
          "flex justify-between px-2 gap-4",
          multiLine && "flex-col"
        )}
      >
        <div>{label}</div>
        <div>{content}</div>
      </div>
    );
    return variant === "secondary" ? (
      <Typography variant="caption" color="textSecondary" component="div">
        {row}
      </Typography>
    ) : (
      row
    );
  };

  const startDate = job.startedAt
    ? DateTime.fromISO(job.startedAt).toJSDate().toLocaleDateString()
    : t("utils.descriptors.none");

  const farmName = farm?.name;
  const fieldName = farm?.zones.find((z) => z.id?.id === job.fieldId)?.name;
  const pathZoneName = configuredJob?.transport?.pathId
    ? farm?.zones.find((z) => z.id?.id === configuredJob.transport.pathId)?.name
    : undefined;

  const zoneName = configuredJob?.transport ? pathZoneName : fieldName;

  const coalesceNone = (a?: ReactNode): ReactNode => {
    return a ?? t("utils.descriptors.none");
  };

  const pathPlanner =
    [job.pathPlannerSource, job.pathPlannerVersion].filter(Boolean).join(" ") ||
    undefined;

  return (
    <div className="flex flex-col gap-2">
      <Typography variant="h6" className="p-2">
        {stateStatus?.text}
      </Typography>
      {rowElement(
        capitalize(t("models.farms.zone_one")),
        coalesceNone(zoneName)
      )}
      {rowElement(
        capitalize(t("models.farms.farm_one")),
        coalesceNone(farmName)
      )}
      {rowElement(
        capitalize(t("components.DateRangePicker.startDate")),
        startDate
      )}
      {rowElement(capitalize(t("utils.descriptors.type_one")), jobType?.text)}
      {rowElement(
        t("views.autotractor.pathPlanner"),
        coalesceNone(pathPlanner),
        { variant: "secondary" }
      )}
      <Divider />
      {/* 
        only display the bits about the rollout of the new activities system in
        ground prep job details, since for LW/transport jobs there are no
        per-job overrides yet
        */}
      {job.type === JobType.GROUND_PREP && activities.length > 0 && (
        <Alert severity="info" className="mx-2">
          {t("views.autotractor.jobDetails.newActivityRolloutNote")}
        </Alert>
      )}
      <div className="flex flex-col px-2 gap-2">
        <Typography variant="subtitle1">
          {t("views.autotractor.jobDetails.jobWideConfigurationGroup")}
          {job.type === JobType.GROUND_PREP && <RolloutChip variant="-6.5" />}
        </Typography>
        {(job.type === JobType.GROUND_PREP ||
          job.type === JobType.LASER_WEED ||
          job.type === JobType.TRANSPORT ||
          job.type === JobType.IN_SEASON) && (
          <>
            <SpeedEditModal
              open={showSpeedEditModal}
              job={job}
              onClose={() => setShowSpeedEditModal(false)}
              onSave={() => setShowSpeedEditModal(false)}
            />
            <Button
              className="justify-start"
              color="secondary"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setShowSpeedEditModal(true)}
            >
              {t("utils.descriptors.speed_one")}
            </Button>
          </>
        )}
        {job.type === JobType.GROUND_PREP && (
          <>
            <ActivitiesConfigModal
              open={showActivitiesConfigModal}
              jobId={job.id}
              onClose={() => setShowActivitiesConfigModal(false)}
            />
            <Button
              className="justify-start"
              color="secondary"
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setShowActivitiesConfigModal(true)}
            >
              {t("views.autotractor.autonomousActivities.title")}
            </Button>
          </>
        )}
        {job.type === JobType.LASER_WEED && (
          <>
            <AutoCompleteRowsModal
              open={showAutoCompleteRowsModal}
              job={job}
              availableTractors={availableTractors?.robots}
              onClose={() => setShowAutoCompleteRowsModal(false)}
              onSave={() => setShowAutoCompleteRowsModal(false)}
            />
            <AlignAllRowsModal
              open={showAlignAllRowsModal}
              job={job}
              onClose={() => setShowAlignAllRowsModal(false)}
              onSave={() => setShowAlignAllRowsModal(false)}
            />
            <Button
              className="justify-start"
              color="secondary"
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={() => setShowAutoCompleteRowsModal(true)}
            >
              {t("views.autotractor.autoCompleteRows.title")}
            </Button>
            <Button
              className="justify-start"
              color="secondary"
              variant="contained"
              startIcon={<CalibrateIcon />}
              onClick={() => setShowAlignAllRowsModal(true)}
            >
              {t("views.autotractor.alignAllRows.title")}
            </Button>
          </>
        )}
      </div>
      {job.type === JobType.GROUND_PREP && activities.length > 0 && (
        <M6ActivitiesConfigEditors jobId={job.id} activities={activities} />
      )}
    </div>
  );
};
